import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import carconsultoria from '../assets/StatCarr/carconsultoria.webp';
import capvirtual from '../assets/StatCarr/capvirtual.webp';
import cappresencial from '../assets/StatCarr/cappresencial.webp';

const Carrousel = () => {
    const { t } = useTranslation();

    const [currentPanel, setCurrentPanel] = useState('capacitacion');
    const [currentFlipcardIndex, setCurrentFlipcardIndex] = useState(0);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [flippedCards, setFlippedCards] = useState(new Set());
    const flipIntervalRef = useRef(null);
    const touchStartXRef = useRef(0);

    useEffect(() => {
        const handleResize = () => {
            const newIsMobile = window.innerWidth <= 768;
            setIsMobile(newIsMobile);

            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
                flipIntervalRef.current = null;
            }

            if (newIsMobile) {
                setCurrentFlipcardIndex(0);
                setFlippedCards(new Set());
            } else if (currentPanel === 'capacitacion' && window.innerWidth > 768) {
                startFlipcardAnimation();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPanel]);

    useEffect(() => {
        if (currentPanel === 'capacitacion' && !isMobile && window.innerWidth > 768) {
            startFlipcardAnimation();
        }

        return () => {
            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPanel, isMobile]);

    const handlePanelChange = (panel) => {
        if (flipIntervalRef.current) {
            clearInterval(flipIntervalRef.current);
            flipIntervalRef.current = null;
        }

        if (panel === 'capacitacion' && !isMobile && window.innerWidth > 768) {
            startFlipcardAnimation();
        } else {
            setFlippedCards(new Set());
        }

        setCurrentPanel(panel);
    };

    const startFlipcardAnimation = () => {
        if (!isMobile && window.innerWidth > 768) {
            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
            }

            flipIntervalRef.current = setInterval(() => {
                flipNextCard();
            }, 5000);
        }
    };

    const flipNextCard = () => {
        const nextIndex = (currentFlipcardIndex + 1) % 2;
        setFlippedCards(prev => {
            const newSet = new Set(prev);
            newSet.add(currentFlipcardIndex);
            return newSet;
        });
        setCurrentFlipcardIndex(nextIndex);
    };

    const handleFlipCard = (cardIndex, action) => {
        setFlippedCards(prev => {
            const newSet = new Set(prev);
            if (action === 'flip') {
                newSet.add(cardIndex);
            } else {
                newSet.delete(cardIndex);
            }
            return newSet;
        });

        if (currentPanel === 'capacitacion' && !isMobile && window.innerWidth > 768) {
            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
            }
            startFlipcardAnimation();
        }
    };

    const handleMouseEnter = () => {
        if (!isMobile && flipIntervalRef.current) {
            clearInterval(flipIntervalRef.current);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile && currentPanel === 'capacitacion') {
            startFlipcardAnimation();
        }
    };

    const showFlipcard = (index) => {
        setCurrentFlipcardIndex(index);
    };

    const navigateFlipcards = (direction) => {
        let newIndex = currentFlipcardIndex + direction;

        if (newIndex < 0) {
            newIndex = 1;
        } else if (newIndex >= 2) {
            newIndex = 0;
        }

        showFlipcard(newIndex);
    };

    const handleTouchStart = (e) => {
        touchStartXRef.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].screenX;
        const touchStartX = touchStartXRef.current;

        if (touchEndX < touchStartX - 50) {
            navigateFlipcards(1);
        } else if (touchEndX > touchStartX + 50) {
            navigateFlipcards(-1);
        }
    };

    const virtualModules = t('carrousel.training.virtual.modules', { returnObjects: true });
    const inPersonModules = t('carrousel.training.in_person.modules', { returnObjects: true });

    return (
        <div>
            <div className="divider-conteiner text-center space-y-0 mb:space-y-2 mb-8">
                <h1 className="font-sans text-secondary font-bold resp-h3">
                    {t('carrousel.title')}
                </h1>
                <p className="hero-p resp-h2" style={{ lineHeight: "130%" }}>
                    {t('carrousel.subtitle')}
                </p>
            </div>

            <div className="carrusel-container">
                <div className="carrusel-menu">
                    <button
                        className={`menu-btn ${currentPanel === 'capacitacion' ? 'active' : ''}`}
                        onClick={() => handlePanelChange('capacitacion')}
                    >
                        {t('carrousel.menu_training')}
                    </button>
                    <button
                        className={`menu-btn ${currentPanel === 'consultoria' ? 'active' : ''}`}
                        onClick={() => handlePanelChange('consultoria')}
                    >
                        {t('carrousel.menu_consulting')}
                    </button>
                </div>

                <div className="carrusel-content">
                    {/* Panel de Consultoría */}
                    <div className={`content-panel ${currentPanel === 'consultoria' ? 'active' : ''}`}>
                        <div className="card-container">
                            <div className="card-content">
                                <h2 className="card-title">{t('carrousel.consulting.title')}</h2>
                                <p className="card-body">{t('carrousel.consulting.body')}</p>
                                <p className="card-body-bold">{t('carrousel.consulting.body_bold')}</p>
                                <button className="cta-btn" onClick={() => {
                                    const message = encodeURIComponent(t('carrousel.consulting.whatsapp_message'));
                                    window.open(`https://wa.me/573155381468?text=${message}`, '_blank');
                                }}>{t('carrousel.consulting.cta')}</button>
                            </div>
                            <div className="card-image">
                                <img src={carconsultoria} alt="Consultoría" />
                            </div>
                        </div>
                    </div>

                    {/* Panel de Capacitación */}
                    <div className={`content-panel ${currentPanel === 'capacitacion' ? 'active' : ''}`}>
                        <div className="mobile-nav-indicators">
                            <div className="indicators">
                                <span className={`indicator ${currentFlipcardIndex === 0 ? 'active' : ''}`} onClick={() => showFlipcard(0)}></span>
                                <span className={`indicator ${currentFlipcardIndex === 1 ? 'active' : ''}`} onClick={() => showFlipcard(1)}></span>
                            </div>
                        </div>

                        <div
                            className="flipcards-container"
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            {/* Flipcard Virtual */}
                            <div
                                className={`flipcard ${isMobile ? (currentFlipcardIndex === 0 ? 'active' : '') : ''} ${flippedCards.has(0) ? 'flipped' : ''}`}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div className="flipcard-inner">
                                    <div className="flipcard-front">
                                        <div className="flipcard-image">
                                            <img src={capvirtual} alt={t('carrousel.training.virtual.title')} />
                                        </div>
                                        <div className="flipcard-content">
                                            <h3 className="flipcard-title">{t('carrousel.training.virtual.title')}</h3>
                                            <p>{t('carrousel.training.virtual.description')}</p>
                                            <div className="highlight-box">
                                                <p>{t('carrousel.training.virtual.highlight')}</p>
                                            </div>
                                            <button
                                                className="flip-btn"
                                                onClick={() => handleFlipCard(0, 'flip')}
                                            >
                                                {t('carrousel.training.see_details')}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flipcard-back">
                                        <h3 className="back-title">{t('carrousel.training.virtual.title')}</h3>
                                        <div className="back-content">
                                            <div className="highlight-box">
                                                <p>{t('carrousel.training.virtual.back_highlight')}</p>
                                            </div>
                                            <div className="table-container">
                                                <table className="data-table">
                                                    <tbody>
                                                        {virtualModules.map((mod, idx) => (
                                                            <tr key={idx}>
                                                                <td className="topic">{mod.topic}</td>
                                                                <td className="number">{mod.duration}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <button
                                            className="flip-btn"
                                            onClick={() => handleFlipCard(0, 'unflip')}
                                        >
                                            {t('carrousel.training.go_back')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Flipcard Presencial */}
                            <div
                                className={`flipcard ${isMobile ? (currentFlipcardIndex === 1 ? 'active' : '') : ''} ${flippedCards.has(1) ? 'flipped' : ''}`}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div className="flipcard-inner">
                                    <div className="flipcard-front">
                                        <div className="flipcard-image">
                                            <img src={cappresencial} alt={t('carrousel.training.in_person.title')} />
                                        </div>
                                        <div className="flipcard-content">
                                            <h3 className="flipcard-title">{t('carrousel.training.in_person.title')}</h3>
                                            <p>{t('carrousel.training.in_person.description')}</p>
                                            <div className="highlight-box">
                                                <p>{t('carrousel.training.in_person.highlight')}</p>
                                            </div>
                                            <button
                                                className="flip-btn"
                                                onClick={() => handleFlipCard(1, 'flip')}
                                            >
                                                {t('carrousel.training.see_details')}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flipcard-back">
                                        <h3 className="back-title">{t('carrousel.training.in_person.title')}</h3>
                                        <div className="back-content">
                                            <div className="highlight-box">
                                                <p>{t('carrousel.training.in_person.back_highlight')}</p>
                                            </div>
                                            <div className="table-container">
                                                <table className="data-table">
                                                    <tbody>
                                                        {inPersonModules.map((mod, idx) => (
                                                            <tr key={idx}>
                                                                <td className="topic">{mod.topic}</td>
                                                                <td className="number">{mod.duration}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <button
                                            className="flip-btn"
                                            onClick={() => handleFlipCard(1, 'unflip')}
                                        >
                                            {t('carrousel.training.go_back')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mobile-nav">
                            <button className="nav-arrow prev" onClick={() => navigateFlipcards(-1)}>‹</button>
                            <button className="nav-arrow next" onClick={() => navigateFlipcards(1)}>›</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Carrousel;
